import { useEffect, useState, useCallback } from 'react';
import {
  Stack, Group, Title, Text, Button, Box, Paper, Alert, Loader, Checkbox,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { api } from '../../wheel-api/client';
import { getStandToken } from '../../wheel-api/stand';
import { useKiosk } from '../../context/KioskContext/KioskContext';
import { OpcionesGrandes } from './OpcionesGrandes';
import { TecladoNumerico } from './TecladoNumerico';
import { CapturaFoto } from './CapturaFoto';
import { ForzarEntrega } from './ForzarEntrega';
import { BotonesImpresion } from './BotonesImpresion';
import { componerFotoCaravana } from '../../utils/impresion';
import { getDeviceId } from './device';
import { UI, BRAND } from '../../theme/kioskTheme';

const DIAS = [
  { value: 'MIE', label: 'Miércoles' },
  { value: 'JUE', label: 'Jueves' },
  { value: 'VIE', label: 'Viernes' },
];

/**
 * Paso de caravana dentro del flujo de la ruleta (gemelos §5.1 sobre el núcleo
 * compartido). Va después de la ficha y antes de girar: la ficha ya está
 * guardada, así que el objetivo real no corre riesgo, y es el momento de
 * máxima atención porque la persona está esperando para girar.
 *
 * Se saltea solo, sin que nadie lo note, en tres casos:
 *   - la tablet no está armada (no hay bolsa del lado de esta tablet),
 *   - la persona ya tiene una caravana (§7: una por persona),
 *   - no hay bolsa activa (viernes, o fuera de las ventanas de §2).
 *
 * Nunca bloquea el giro: si algo falla, se sigue a la ruleta.
 */
export function CaravanaScreen() {
  const { attendee, destinoFinal } = useKiosk();
  const navigate = useNavigate();

  const siguiente = destinoFinal || '/kiosk/spin';
  const irAGirar = useCallback(
    (reemplazar = false) => navigate(siguiente, { replace: reemplazar }),
    [navigate, siguiente],
  );

  const [estado, setEstado] = useState(null);
  const [numero, setNumero] = useState('');
  const [dias, setDias] = useState([]);
  const [sinFoto, setSinFoto] = useState(false);
  // Solo se usa en días de prueba: en el congreso la mitad la decide el reloj.
  const [mitadForzada, setMitadForzada] = useState(null);
  const [error, setError] = useState(null);
  const [exito, setExito] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const [fotoUrl, setFotoUrl] = useState(null);
  // La hoja se compone apenas se sube la foto, no al tocar imprimir:
  // navigator.share necesita el gesto del usuario sin await previo.
  const [hoja, setHoja] = useState(null);
  // Espejo local de exito.pideFoto: puede pasar a true si autoriza recién ahora.
  const [pideFoto, setPideFoto] = useState(false);
  const [autorizando, setAutorizando] = useState(false);

  // La decisión de mostrarse o no se toma una sola vez, al entrar.
  useEffect(() => {
    let vivo = true;

    // Sin token no se puede entregar: el endpoint pide staff. La tablet se
    // arma una vez desde /kiosk/armar y el token queda guardado.
    if (!getStandToken() || !attendee || attendee.tagNumber) {
      irAGirar(true);
      return undefined;
    }

    api.gemelosEstado()
      .then(e => {
        if (!vivo) return;
        if (!e.abierto) irAGirar(true);
        else setEstado(e);
      })
      // Si no podemos leer la bolsa, no dejamos a nadie parado frente a la
      // ruleta: se gira igual y la caravana se entrega en el otro puesto.
      .catch(() => { if (vivo) irAGirar(true); });

    return () => { vivo = false; };
  }, [attendee, irAGirar]);

  async function entregar(forzado = null) {
    setEnviando(true);
    setError(null);
    try {
      const r = await api.gemelosEntregar({
        number: Number(numero),
        attendeeId: attendee._id,
        daysPresent: dias,
        sinFoto,
        // En un día de prueba la elige el selector; al forzar, el panel.
        mitadForzada: forzado?.mitad || mitadForzada,
        deviceId: getDeviceId(),
        ...(forzado ? {
          forzar: true,
          pinDireccion: forzado.pinDireccion,
          motivo: forzado.motivo,
        } : {}),
      });
      setExito(r);
      setPideFoto(Boolean(r.pideFoto));
    } catch (err) {
      // El objeto entero: trae `forzable` y `pideMitad`.
      setError(err.data || { message: 'No pudimos entregar la caravana.' });
    } finally {
      setEnviando(false);
    }
  }


  // El permiso de imagen se puede dar en el momento: la persona está parada
  // enfrente. Solo suma permiso, nunca lo saca.
  async function autorizar() {
    setAutorizando(true);
    try {
      await api.gemelosAutorizarImagen(exito.tagId);
      setSinFoto(false);
      setPideFoto(true);
    } catch (err) {
      notifications.show({
        message: err.data?.message || 'No pudimos registrar la autorización.',
        color: 'red',
      });
    } finally { setAutorizando(false); }
  }

  // ---- Entregada: foto y a girar ----------------------------------------
  if (exito) {
    return (
      <Stack align="center" justify="center" style={{ flex: 1 }} gap="lg" px="8%">
        <Title order={1} ta="center" c="forvet.7" style={{ fontSize: 'clamp(26px, 4.6vh, 46px)' }}>
          {exito.cerroPar ? '¡TU GEMELO YA ESTÁ EN EL EVENTO!' : 'ESTA CARAVANA ES TUYA'}
        </Title>

        <Paper withBorder radius="lg" p="lg" style={{ borderWidth: 3, borderColor: UI.accion }}>
          <Text ta="center" c="forvet.6" size="md">Número</Text>
          <Text ta="center" fw={900} style={{ fontSize: 'clamp(44px, 8vh, 84px)', lineHeight: 1 }}>
            {String(exito.numero).padStart(3, '0')}
          </Text>
        </Paper>

        <Text ta="center" size="lg" c="forvet.6" maw={640}>
          Hay otra persona con este mismo número. Encontrala y ganan los dos.
        </Text>

        {pideFoto ? (
          <Stack align="center" gap="xs">
            <CapturaFoto
              label={fotoUrl ? 'REPETIR LA FOTO' : 'SACARLE LA FOTO'}
              subiendo={subiendoFoto}
              urlActual={fotoUrl}
              onSubir={async (blob) => {
                setSubiendoFoto(true);
                try {
                  const r = await api.gemelosSubirFoto(exito.tagId, blob);
                  setFotoUrl(r.url);
                  // Se compone desde el blob local: la URL de S3 ensuciaría el
                  // canvas y toBlob fallaría por CORS.
                  setHoja(await componerFotoCaravana({
                    blob, numero: exito.numero, nombre: exito.nombre,
                  }));
                } catch (err) {
                  notifications.show({
                    message: err.data?.message || 'No pudimos subir la foto. Probá de nuevo.',
                    color: 'red',
                  });
                } finally { setSubiendoFoto(false); }
              }}
            />
            {!fotoUrl && (
              <Text size="sm" c="forvet.6" ta="center" maw={520}>
                Es la foto que va al muro: le sirve al gemelo para reconocerte.
              </Text>
            )}
            {fotoUrl && (
              <BotonesImpresion
                blob={hoja}
                nombreArchivo={`caravana-${String(exito.numero).padStart(3, '0')}.png`}
                titulo={`Caravana ${String(exito.numero).padStart(3, '0')}`}
              />
            )}
          </Stack>
        ) : (
          <Stack align="center" gap="sm" maw={640}>
            <Alert color="gray" radius="md">
              <Text fw={700}>No le vamos a sacar la foto</Text>
              <Text size="sm">
                {sinFoto
                  ? 'Marcaste que no quiere foto.'
                  : 'En la ficha no autorizó el uso de su imagen (el casillero viene destildado y es fácil pasarlo por alto).'}
                {' '}Sin foto, su gemelo se queda con una sola pista para reconocerla.
              </Text>
            </Alert>
            <Button variant="outline" size="sm" loading={autorizando} onClick={autorizar}>
              SI AHORA ACEPTA, AUTORIZAR Y SACAR LA FOTO
            </Button>
          </Stack>
        )}

        <Button h={78} w={400} onClick={() => irAGirar()}>AHORA SÍ, GIRAR</Button>
      </Stack>
    );
  }

  if (!estado) {
    return <Stack align="center" justify="center" style={{ flex: 1 }}><Loader size="xl" /></Stack>;
  }

  const listo = numero.length > 0 && Number(numero) > 0 && dias.length > 0;

  return (
    <Stack style={{ flex: 1, minHeight: 0 }} px="6%" gap="sm">
      {estado.prueba && (
        <Alert color="orange" radius="md" py={6}>
          <Text fw={700}>MODO PRUEBA — la ventana de bolsas está abierta a la fuerza.
          Apagá GEMELOS_VENTANA_LIBRE antes del congreso.</Text>
        </Alert>
      )}

      <Stack gap={2} pt="xs">
        <Title order={2} ta="center" c={BRAND.forvetNavy}
               style={{ fontSize: 'clamp(22px, 3.6vh, 38px)' }}>
          ANTES DE GIRAR, LLEVATE UNA CARAVANA
        </Title>
        <Text ta="center" size="lg" c="forvet.6">
          Hay otra persona en el congreso con tu mismo número. Si se encuentran, ganan los dos.
        </Text>
      </Stack>


      {estado.prueba && (
        <Paper radius="md" p="xs" withBorder>
          <Group gap="sm" wrap="wrap">
            <Text fw={600} fz="var(--fs-small)">Día de prueba — elegí la mitad:</Text>
            <OpcionesGrandes
              options={[{ value: 'A', label: 'Mitad A' }, { value: 'B', label: 'Mitad B' }]}
              value={mitadForzada}
              onChange={setMitadForzada}
            />
          </Group>
        </Paper>
      )}

      {/* overflow auto: con el panel de forzado abierto el contenido crece
          y sin esto empuja los botones de abajo fuera de la pantalla, que
          en una tablet deja al operador sin forma de cancelar. */}
      <Group align="flex-start" gap="xl"
             style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <Stack gap={4} style={{ flex: '0 0 300px' }}>
          <Group justify="space-between" align="baseline">
            <Text size="lg" fw={600} c="forvet.8">Número</Text>
            {/* El operador no elige la bolsa: la decide el horario (§2). */}
            <Text size="sm" c="forvet.6">bolsa {estado.bolsa}</Text>
          </Group>
          <TecladoNumerico value={numero} onChange={v => { setNumero(v); setError(null); }} />
        </Stack>

        <Stack gap="md" style={{ flex: 1 }}>
          <Box>
            <Text size="lg" fw={600} c="forvet.8" mb="xs">¿Qué días venís?</Text>
            <OpcionesGrandes multiple options={DIAS} value={dias} onChange={setDias} />
          </Box>

          {attendee.consentImage === false ? (
            <Alert color="gray" radius="md">
              No autorizó el uso de su imagen. Juega igual, sin foto.
            </Alert>
          ) : (
            <Checkbox
              size="lg" checked={sinFoto}
              onChange={e => setSinFoto(e.currentTarget.checked)}
              label="No quiere foto — juega igual"
            />
          )}

          <ForzarEntrega error={error} enviando={enviando}
                         onForzar={f => entregar(f)} />
        </Stack>
      </Group>

      <Group grow pb="sm">
        {/* Saltear tiene que ser barato: nadie se queda sin girar por esto. */}
        <Button variant="outline" h={72} onClick={() => irAGirar()}>
          AHORA NO, GIRAR
        </Button>
        <Button h={72} disabled={!listo} loading={enviando} onClick={() => entregar()}>
          LLEVÁRMELA
        </Button>
      </Group>
    </Stack>
  );
}
